import { NavigationService } from "@app/services/navigation.service";
import { Component, Input, OnChanges, OnDestroy, OnInit } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent, IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { FilterBarService } from "@app/directives/_resourceFilters/filter-bar/filter-bar.service";
import { SortDirection } from "@app/enum/SortDirection";
import { IAccount } from "@app/services/account.service";
import { ConstantsService } from "@app/services/constantsService";
import { DateService, DateUnit } from "@app/services/date.service";
import { TranslationService } from "@app/services/translation.service";
import { combineLatest, forkJoin, lastValueFrom, Subscription } from "rxjs";
import { EngineerJobStatus, IEngineerJob, IEngineerJobBookingWithEngineerMappings, IEngineerJobItem } from "../../foundation.interfaces";
import { AccountService } from "../../_services/account.service";
import { EngineerJobItemService } from "../../_services/engineer-job-item.service";
import { EngineerJobService } from "../../_services/engineer-job.service";
import { JobCompletionComponent } from "./job-completion/job-completion.component";
import './job-list.component.scss';
import { EngineerJobBookingService } from "../../_services/engineer-job-booking.service";
import { FoundationNavigationModule } from "../../foundation.navigation";
import { IVTableCell } from "@app/directives/_layout/v-table/_models/IVTableCell";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: 'job-list',
    template: require('./job-list.component.html')
})
export class JobListComponent implements OnInit, OnChanges {

    @Input() customerId: number;

    //--


    jobs: IEngineerJob[] = [];
    jobBookings: IEngineerJobBookingWithEngineerMappings[];
    displayJobs: IEngineerJob[] = [];
    accounts: IAccount[] = [];
    selectedJob: IEngineerJob;

    jobItems: IEngineerJobItem[];

    tabCount: number = 0;

    loading = false;
    isMobile = false;

    startDate: Date = new Date();
    endDate: Date = new Date();

    showAddJobForm: boolean;

    //Table
    tableColumns: IVTableColumn<IEngineerJob>[];
    tableRows: IVTableRow[];

    filterTabs: IFilterBarTab<IEngineerJob>[] = [];

    dataSubscription: Subscription;
    jobBookingsSubscription: Subscription;

    constructor(
        private readonly navigationService: NavigationService,
        private readonly accountService: AccountService,
        private readonly constantsService: ConstantsService,
        private readonly dateService: DateService,
        private readonly engineerJobBookingService: EngineerJobBookingService,
        private readonly engineerJobItemService: EngineerJobItemService,
        private readonly engineerJobService: EngineerJobService,
        private readonly filterBarService: FilterBarService,
        private readonly translationService: TranslationService,
        private readonly vTableService: VTableService
    ) { }

    ngOnInit(): void {
        this.isMobile = this.constantsService.getIsMobile();
        this.startDate = this.dateService.addOrSubtractTime(new Date(), -180, DateUnit.Day);
        this.endDate = new Date();
        this.loadData();
        this.createTabs();
    }

    ngOnChanges(): void {
        if (this.tableColumns) {
            this.createTableRows();
        }
    }

    onFilterChanged(filterEvent: IAggregateFilterEvent<IEngineerJob>): void {
        if (filterEvent.trigger == "date") {
            this.startDate = filterEvent.date[0];
            this.endDate = filterEvent.date[1];
            this.filterJobsByDate();
        }
    }

    selectJob(row: IVTableRow): void {
        const job = row.data;
        this.navigationService.go(FoundationNavigationModule.JobView, { jobId: job.Id });
    }

    addJob(): void {
        this.showAddJobForm = true;
        this.selectedJob = this.engineerJobService.createNewJob();
    }

    closeJobForm(): void {
        this.showAddJobForm = false;
    }

    jobBookingSaved(jobBooking: IEngineerJobBookingWithEngineerMappings): void {
        this.jobBookings.push(jobBooking);
        this.displayJobs.find(job => job.Id == jobBooking.JobId).jobBooking = jobBooking;
        
        this.createTableRows();
        this.createTabs();

        this.closeJobForm();
        this.navigationService.go(FoundationNavigationModule.JobView, { jobId: jobBooking.JobId }, false);
    }

    //--

    private loadData(): void {
        this.loading = true;
        this.createTable();

        if (this.dataSubscription) {
            this.dataSubscription.unsubscribe();
        }

        this.dataSubscription = new Subscription();

        this.dataSubscription.add(combineLatest([
            this.accountService.getCustomerAccounts(),
            this.engineerJobService.getEngineerJobs(),
            this.engineerJobBookingService.getEngineeringJobBookingsWithMappings({ Start: this.startDate, End: this.dateService.addOrSubtractTime(new Date(), 180, DateUnit.Day) })
        ]).pipe(untilDestroyed(this))
            .subscribe(([accounts, jobs, bookings]) => {
                this.accounts = accounts;
                this.jobBookings = bookings;

                this.renderJobs(jobs);
                this.loading = false
            }));            

        this.dataSubscription.add(this.engineerJobBookingService.jobBookings.pipe(untilDestroyed(this)).subscribe(bookings => {
            this.jobBookings = bookings;
            this.renderJobs(this.jobs);
        }));
    }

    private renderJobs(jobs: IEngineerJob[]): void {
        this.loadJobs(jobs);
        this.createTableRows();
    }

    private loadJobs(jobs: IEngineerJob[]): void {
        if (!jobs?.length) {
            return;
        }

        const filteredJobs = this.customerId ? jobs.filter(j => j.AccountId == this.customerId) : jobs;

        this.jobs = this.displayJobs = this.engineerJobService.mapAccountsToJobs(filteredJobs, this.accounts);
        this.filterJobsByDate();
        this.engineerJobService.mapBookingsToJobs(this.displayJobs, this.jobBookings);
        this.tabCount = this.displayJobs?.length;
    }

    private filterJobsByDate(): void {
        if (!this.startDate || !this.endDate || !this.jobs) {
            return;
        }

        this.displayJobs = this.jobs.filter(job => {
            return this.dateService.dateIsBetweenDates(job.CreatedDate, this.startDate, this.dateService.addOrSubtractTime(this.endDate, 1, DateUnit.Hour));
        });
    }

    private createTable(): void {

        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({
                    title: "ID",
                    width: 80
                }),
                createCellFunction: (job) => this.vTableService.createTableCell(job.Id.toString())


            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "EngineerJobs.Customer",
                    minTableWidth: 950
                }),
                createCellFunction: (job) => this.vTableService.createTableCell(this.engineerJobService.getCompanyNameForJob(job, this.accounts))

            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "Common.Status",
                    width: 90
                }),
                createCellFunction: (job) => this.vTableService.createEnumCell(EngineerJobStatus, 'EngineerJobStatus', job.Status)

            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Description" }),
                createCellFunction: (job) => this.vTableService.createTableCell(job.Description, { tooltip: true })
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "EngineerJobs.Assigned",
                    minTableWidth: 950
                }),
                createCellFunction: (job) => this.getEngineerAssignCell(job)
            },
            {
                header: this.vTableService.createDateTableHeader({
                    tKey: "Common.StartTime",
                    minTableWidth: 950,
                    sorted: true,
                    sortDirection: SortDirection.Descending
                }),
                createCellFunction: (job) => this.vTableService.createDateCell(job.jobBooking?.StartUtc, { dateTime: false })
            },
            {
                header: this.vTableService.createDateTableHeader({
                    tKey: "Common.EndTime",
                    minTableWidth: 950
                }),
                createCellFunction: (job) => this.vTableService.createDateCell(job.jobBooking?.EndUtc, { dateTime: false })
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "EngineerJobs.Completion",
                    sortable: false,
                    searchable: false,
                    width: 150
                }),
                createCellFunction: (job) => this.vTableService.createTableCell(job, { component: JobCompletionComponent, componentInputs: { jobId: job.Id }, exportDataMethod: () => this.exportJobCompletionMethod(job.Id) })
            }
        ]

    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.displayJobs, this.tableColumns);
    }

    private exportJobCompletionMethod(jobId: number): Promise<string> {
        return lastValueFrom(this.engineerJobItemService.getEngineerJobItems({ JobId: jobId })).then(jobItems => {
            const { completedJobItems, totalJobItems } = this.engineerJobItemService.calculateJobCompletion(jobItems);
            return Promise.resolve(`${completedJobItems}/${totalJobItems} ${this.translationService.t("EngineerJobs.JobItems")}`);
        })
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push(this.filterBarService.createTabDefinition<IEngineerJob>("EngineerJobs.Active", (job) => !this.engineerJobService.jobIsClosed(job)));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IEngineerJob>("EngineerJobs.Closed", (job) => this.engineerJobService.jobIsClosed(job)));
    }

    private buildEngineerAssignedString(job: IEngineerJob): string {
        let engineerNames = "";

        if (!job.jobBooking?.assignedEngineers?.length) {
            return engineerNames;
        }

        job.jobBooking?.assignedEngineers?.forEach((engineer, index) => {
            if (index > 2) {
                return;
            }
            if (index > 1) {
                engineerNames += " " + this.translationService.t("EngineerJobs.AndXMore", job.jobBooking.assignedEngineers.length - 2);
                return;
            }
            if (index == 1) {
                engineerNames += ", ";
            }
            engineerNames += engineer.Description;
        });

        return engineerNames;
    }

    private getEngineerAssignCell(job: IEngineerJob): IVTableCell {
        if (!job.jobBooking) {
            return this.vTableService.createTableCell(null);
        }

        if (this.jobIsAssignedToGroup(job)) {
            return this.vTableService.createTableCell(job.jobBooking.assignedGroup?.Description || "", { icon: "fa-users" });
        }

        const engineerAssignedString = this.buildEngineerAssignedString(job);
        return this.vTableService.createTableCell(engineerAssignedString, { tooltip: true, icon: "fa-user", searchData: this.getEngineerSearchDataString(job) })
    }

    private jobIsAssignedToGroup(job: IEngineerJob): boolean {
        return !!job.jobBooking?.mappings?.some(mapping => !!mapping.UserGroupId) || !!job.jobBooking?.assignedGroup;
    }

    private getEngineerSearchDataString(job: IEngineerJob): string {
        let searchString = "";

        job.jobBooking?.assignedEngineers?.forEach(engineer => {
            searchString += "||||" + engineer.Description;
        })

        return searchString;
    }

}
